import { type FormEvent, useEffect, useId, useRef, useState } from "react";

import {
  errorCodeOf,
  errorMessageOf,
  isSessionExpired,
} from "./patientAuthErrors";
import {
  type OneTimePasswordSender,
  authenticateOneTimePassword,
  resendOneTimePassword,
} from "./patientAuthenticationApi";
import {
  COGNITO_ONE_TIME_PASSWORD_MAIL_SENDER,
  COGNITO_ONE_TIME_PASSWORD_MAIL_SUBJECT,
  SAS_ONE_TIME_PASSWORD_MAIL_SUBJECT,
} from "./patientAuthMessages";
import {
  Actions,
  BlockButton,
  Divider,
  ErrorMessage,
  Field,
  Heading,
  Muted,
  SuccessMessage,
} from "./patientAuthenticationParts";

const CODE_LENGTH = 6;

/** 全角の数字は半角に直し、数字以外を取り除いて 6 桁までにする。 */
export function normalizeOneTimePassword(value: string): string {
  return value
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[^0-9]/g, "")
    .slice(0, CODE_LENGTH);
}

type Props = {
  oneTimeToken: string;
  sender: OneTimePasswordSender;
  /**
   * 認証コードが通った。requirePasswordChange なら初期パスワードの段へ、そうでなければトークンを発行して終える。
   * 返した Promise が終わるまで（トークンの発行を待つ間）、ボタンを押せないままにする。
   */
  onAuthenticated: (
    oneTimeToken: string,
    requirePasswordChange: boolean,
  ) => Promise<void> | void;
  onSessionExpired: (message: string) => void;
  onRestart: () => void;
};

const MailCard = ({ rows }: { rows: [string, string][] }) => (
  <dl className="mb-4 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 text-sm">
    {rows.map(([term, description]) => (
      <div key={term}>
        <dt className="text-xs text-gray-600">{term}</dt>
        <dd className="mb-1.5 font-bold">{description}</dd>
      </div>
    ))}
  </dl>
);

export function PatientOneTimePasswordForm({
  oneTimeToken: initialToken,
  sender: initialSender,
  onAuthenticated,
  onSessionExpired,
  onRestart,
}: Props) {
  const id = useId();
  const [oneTimeToken, setOneTimeToken] = useState(initialToken);
  const [sender, setSender] = useState(initialSender);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [errorTick, setErrorTick] = useState(0);
  const [expired, setExpired] = useState(false);
  const [resent, setResent] = useState(false);
  const [loading, setLoading] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);
  const resendRef = useRef<HTMLButtonElement>(null);

  // エラーを出したら、先頭のエラーのある所へフォーカスを移す（期限切れは「新しいコードを送る」）
  useEffect(() => {
    if (!error) return;
    if (expired) resendRef.current?.focus();
    else codeRef.current?.focus();
  }, [error, errorTick, expired]);

  // 再送が通ったら、欄を押せるように戻した後で認証コードの欄へフォーカスを移す（押せない間の focus は効かないため）
  useEffect(() => {
    if (resent && !loading) codeRef.current?.focus();
  }, [resent, loading]);

  const fail = (e: unknown) => {
    if (isSessionExpired(e)) {
      onSessionExpired(errorMessageOf(e));
      return;
    }
    setError(errorMessageOf(e));
    setErrorTick((tick) => tick + 1);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (code.length !== CODE_LENGTH || expired || loading) return;
    setLoading(true);
    setError("");
    setResent(false);
    let result;
    try {
      result = await authenticateOneTimePassword(oneTimeToken, code);
    } catch (e) {
      const errorCode = errorCodeOf(e);
      if (errorCode === "PATIENT_AUTH_ONE_TIME_PASSWORD_EXPIRED") {
        setExpired(true);
      } else if (errorCode === "PATIENT_AUTH_ONE_TIME_PASSWORD_INVALID") {
        setCode("");
      }
      // 503 は同じコードで送り直せるので、入力を残す
      fail(e);
      setLoading(false);
      return;
    }
    // 次の段へ移るか、トークンを発行して画面を移すまで押せないままにする（使い済みのトークンで送り直させないため）
    await onAuthenticated(result.oneTimeToken, result.requirePasswordChange);
  };

  const resend = async () => {
    if (loading) return;
    setLoading(true);
    setError("");
    setResent(false);
    try {
      const nextToken = await resendOneTimePassword(oneTimeToken);
      setOneTimeToken(nextToken);
      setSender("SAS");
      setExpired(false);
      setCode("");
      setResent(true);
    } catch (e) {
      fail(e);
    } finally {
      setLoading(false);
    }
  };

  const ready = code.length === CODE_LENGTH && !expired && !loading;
  const help = expired
    ? "期限が切れたので、新しいコードを送ってください。"
    : "半角数字6桁。6桁そろうと「次へ」を押せます。";
  const errorId = `${id}-error`;

  const cognitoRows: [string, string][] = [
    ["差出人", COGNITO_ONE_TIME_PASSWORD_MAIL_SENDER],
    ["件名", `${COGNITO_ONE_TIME_PASSWORD_MAIL_SUBJECT}（英語の件名です）`],
    ["有効期限", "送ってから15分"],
  ];

  return (
    <form onSubmit={submit} noValidate>
      <Heading focusOnMount>認証コードの入力</Heading>
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
      {resent && (
        <SuccessMessage role="status">
          新しい認証コードを送りました。
        </SuccessMessage>
      )}
      {sender === "COGNITO" ? (
        <>
          <p className="mb-4 text-sm">
            このログインでは、
            <b>これまでのログインと同じ認証コードのメール</b>
            が届きます。以前のログインの画面で受け取ったコードとは別のものです。メールに書かれた6桁のコードを入力してください。
          </p>
          <MailCard rows={cognitoRows} />
          <p className="mb-4 text-sm text-gray-600">
            このログインでは、件名「{SAS_ONE_TIME_PASSWORD_MAIL_SUBJECT}
            」のメールは届きません。
          </p>
        </>
      ) : (
        <>
          <p className="mb-4 text-sm">
            ご登録のメールアドレスに、6桁の認証コードを送りました。メールに書かれたコードを入力してください。
          </p>
          <MailCard
            rows={[
              ["件名", SAS_ONE_TIME_PASSWORD_MAIL_SUBJECT],
              ["有効期限", "送ってから30分"],
            ]}
          />
        </>
      )}
      <Field
        id={`${id}-code`}
        ref={codeRef}
        label="認証コード（6桁）"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={CODE_LENGTH}
        value={code}
        onChange={(e) => setCode(normalizeOneTimePassword(e.target.value))}
        onPaste={(e) => {
          // 貼り付けは maxLength で切られる前に受け取り、空白や全角を除いてから 6 桁にする（「123 456」の末尾を落とさない）
          e.preventDefault();
          const input = e.currentTarget;
          const start = input.selectionStart ?? code.length;
          const end = input.selectionEnd ?? code.length;
          setCode(
            normalizeOneTimePassword(
              code.slice(0, start) +
                e.clipboardData.getData("text") +
                code.slice(end),
            ),
          );
        }}
        disabled={loading}
        help={help}
        extraDescribedBy={error ? errorId : undefined}
        inputClassName="text-center text-[22px] tracking-[0.5em] pl-[calc(12px+0.5em)] tabular-nums"
      />
      <Actions>
        <BlockButton type="submit" disabled={!ready}>
          次へ
        </BlockButton>
        <BlockButton
          ref={resendRef}
          variant={expired ? "solid" : "outline"}
          onClick={() => void resend()}
          disabled={loading}
        >
          新しいコードを送る
        </BlockButton>
      </Actions>
      {sender === "COGNITO" && (
        <p className="mt-1 text-xs text-gray-600">
          「新しいコードを送る」を押すと、件名「
          {SAS_ONE_TIME_PASSWORD_MAIL_SUBJECT}
          」のメールで届き、有効期限は30分です。
        </p>
      )}
      <Divider />
      <Muted>
        メールが届かないときは、迷惑メールのフォルダもご確認ください。
      </Muted>
      <p className="mt-2">
        <button
          type="button"
          onClick={onRestart}
          className="text-sm text-brandBlue underline"
        >
          最初からやり直す
        </button>
      </p>
    </form>
  );
}
